import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import FormGenerator from '@sharedCR/common/formGenerator';
import ModalFormWrapper from '@sharedCR/coreDesignSystem/ModalFormWrapper';
import { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { FieldType } from 'constants/formGenerator';
import { DenialReasonField } from 'constants/loanActionEditor';
import useForm from 'hooks/useForm';
import { getConfigInfo } from 'utils/loanManagement';

import { selectBorrowerOptionsCommonInfo } from 'store/loanApplications/selectors';
import { selectLoanActionFieldsMap } from 'store/system/selectors';

import DenialReasonFieldsSection from './DenialReasonFieldsSection';
import { getFormConfigForLoanActionVariableFields } from './LoanActionModalForm.config';
import { getDenialReasonSchemaShape, getUpdatedFieldsFromForm } from './LoanActionModalForm.utils';

const LoanActionModalForm = ({
  actionId,
  isLoading,
  setIsLoading,
  onFormSubmit,
  formRef,
  variableFields,
}) => {
  const loanActionFieldsMap = useSelector(selectLoanActionFieldsMap);
  const borrowerOptions = useSelector(selectBorrowerOptionsCommonInfo);

  const [[denialReasonField], fields] = useMemo(
    () => _.partition(variableFields, ['fieldId', DenialReasonField.value]),
    [variableFields],
  );

  const hasDenialReasonField = !!denialReasonField;

  const config = useMemo(
    () => getFormConfigForLoanActionVariableFields({ fields, loanActionFieldsMap }),
    [loanActionFieldsMap, fields],
  );

  const { schema } = useMemo(() => getConfigInfo({ applicationData: {}, config }), [config]);

  const initialFormValues = useMemo(() => {
    const [loanActionsGroup] = config.groups;

    return loanActionsGroup.fields.reduce((acc, { type, nameInModel, inputProps }) => {
      if (type === FieldType.SELECT && inputProps.multiple) {
        acc[nameInModel] = [];

        return acc;
      }

      if (type === FieldType.CHECKBOX) {
        acc[nameInModel] = false;

        return acc;
      }

      acc[nameInModel] = null;

      return acc;
    }, {});
  }, [config]);

  const {
    form,
    setSchema,
    handleChange,
    validateField,
    validateForm,
    validationErrors,
    setValidationErrors,
    setInForm,
    isFormChanged,
  } = useForm(initialFormValues);

  useEffect(() => {
    setSchema(
      hasDenialReasonField
        ? schema.shape(getDenialReasonSchemaShape(denialReasonField.required))
        : schema,
    );
  }, [borrowerOptions, denialReasonField, hasDenialReasonField, schema, setSchema]);

  const onSubmit = useCallback(
    async e => {
      e.preventDefault();

      if (!(await validateForm())) {
        return;
      }

      setIsLoading(true);

      const updatedFields = getUpdatedFieldsFromForm({
        form,
        loanActionFieldsMap,
        fields,
        denialReasonField,
      });

      onFormSubmit({ updatedFields, actionId }).finally(() => setIsLoading(false));
    },
    [
      validateForm,
      setIsLoading,
      form,
      loanActionFieldsMap,
      fields,
      denialReasonField,
      onFormSubmit,
      actionId,
    ],
  );

  return (
    <ModalFormWrapper
      formRef={formRef}
      formTestId="loan-editor-action-fields-form"
      isFormChanged={isFormChanged}
      isSubmitting={isLoading}
      onSubmit={onSubmit}
    >
      <div className="min-w-sm">
        <FormGenerator
          config={config}
          form={form}
          handleChange={handleChange}
          isLoading={isLoading}
          numberOfColumns={1}
          setInForm={setInForm}
          validateField={validateField}
          validationErrors={validationErrors}
        />
      </div>
      {hasDenialReasonField && (
        <DenialReasonFieldsSection
          field={denialReasonField}
          formDenialReasons={form.denialReasons}
          handleChange={handleChange}
          isLoading={isLoading}
          setInForm={setInForm}
          setValidationErrors={setValidationErrors}
          validationErrors={validationErrors}
        />
      )}
    </ModalFormWrapper>
  );
};

LoanActionModalForm.propTypes = {
  ...WithLoadingPropTypes,
  formRef: PropTypes.shape({ current: PropTypes.object }).isRequired,
  onFormSubmit: PropTypes.func.isRequired,
};

export default memo(LoanActionModalForm);
